import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Offer } from 'cityads-v3/types/offers';
import { RootState } from 'cityads-v3/types/reducer';

import {
  getOfferById
} from 'cityads-v3/actions/offers';

import Dialog from 'cityads-v3/components/Dialog/Dialog';
import Typography from '@mui/material/Typography';
import OfferGetLink from './OfferGetLink';
import AdvertisingMarkingDialog from './AdvertisingMarking/AdvertisingMarkingDialog';

import classes from './GetLinkDialog.scss';

type GetLinkDialogProps = {
  regexpGetActionLink?: RegExp
  offerId?: Offer['id']
  open?: boolean
  onClose?: (evt: {}, reason: string) => boolean
  dialogClassName?: string
  dialogTitle?: string
  dialogPrompt?: JSX.Element
  OfferGetLinkProps?: typeof OfferGetLink.propTypes
}

export default function GetLinkDialog({
  regexpGetActionLink = /(\/publisher\/offers(?:\/favorites)?)\/([0-9]{1,})\/getActionLink/g,
  offerId,
  open,
  onClose,
  dialogClassName,
  dialogTitle,
  dialogPrompt,
  OfferGetLinkProps,
}: GetLinkDialogProps): JSX.Element {
  const { t } = useTranslation(['Offers']);
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  const offersById = useSelector((state: RootState) => state.offers.offersById);
  const isMobile = useSelector((state: RootState) => state.app.isMobile);
  const user = useSelector((state: RootState) => state.auth.user);

  const isUrlMatching = location.pathname.match(regexpGetActionLink)
  const id = offerId || location.pathname.split(regexpGetActionLink)[2];

  const offer = (id && offersById[id]) || null;

  useEffect(() => {
    if (id && !offer) {
      dispatch(getOfferById(id));
    }
  }, [dispatch, id, offer]);

  const [cachedId, setCachedId] = useState(id);
  const [cachedOffer, setCachedOffer] = useState(offer);

  useEffect(() => {
    if (id) {
      setCachedId(id);
    }
  }, [id, setCachedId]);

  useEffect(() => {
    if (offer) {
      setCachedOffer(offer);
    }
  }, [offer, setCachedOffer]);

  const closeDialog = useCallback((evt: unknown, reason: string) => {
    if (onClose) {
      if (onClose(evt, reason) === false) {
        return;
      }
    }

    history.push({
      ...location,
      pathname: location.pathname.split(regexpGetActionLink)[1]
    });
  }, [onClose, history, location, regexpGetActionLink]);

  const offerTitle = dialogTitle || (
    <div className={classes.titleContent}>
      <Typography variant="body1">
        <a href={cachedOffer?.site_url} target="_blank" rel="noopener noreferrer">
          {cachedOffer?.name}
        </a>
      </Typography>
      <Typography variant="body2" classes={{ root: classes.titleCta }}>{t('exports.setupLink')}:</Typography>
    </div>
  );

  if (user?.isBanned) {
    return null;
  }

  const isOpen = !!(user && cachedId && cachedOffer && (open || isUrlMatching));

  return (
    <>
      <Dialog
        open={isOpen}
        title={offerTitle}
        showLogo={false}
        onRequestClose={closeDialog}
        classes={{
          paper: classNames(classes.dialogPaper, dialogClassName)
        }}
        fullScreen={isMobile}
      >
        {dialogPrompt}

        {(cachedId && cachedOffer) ?
          (
            <OfferGetLink
              isInDialog
              offerId={+cachedId}
              isDeeplinkEnabled={cachedOffer?.is_deeplink_enabled}
              isGoogleAdsEnabled={cachedOffer?.is_google_ads_enabled}
              isMicrosoftAdsEnabled={cachedOffer?.is_microsoft_ads_enabled}
              {...OfferGetLinkProps}
            />
          )
          :
          null
        }
      </Dialog>

      <AdvertisingMarkingDialog open={false} offerId={+cachedId} />
    </>
  );
}
